import { Typography as MaterialTypography } from '@mui/material';
import React, { useMemo } from 'react';

import { DEFAULT_MARGIN_TEXT } from '../Spacer/Spacer';
import type { CustomVariant, TypographyProps } from './Typography.types';

const SSR_SAFE_FIRST_CHILD_SELECTOR =
  '&:first-child, body.if6 &:first-child /* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */';

const SSR_SAFE_LAST_CHILD_SELECTOR =
  '&:last-child, body.if6 &:last-child /* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */';

export const CUSTOM_VARIANT_MAPPING: {
  [variant in CustomVariant]: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'div';
} = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',

  button: 'span', // B1
  error: 'div', // B1*

  body1: 'div', // P1
  body2: 'div', // P2

  c1: 'h1',
  c2: 'h2',
  c3: 'h3',

  responsive1: 'div', // Mobile: b1 bold | Desktop: h5
  responsive2: 'div', // Mobile: h6 | Desktop: h4

  openerFullPage: 'h1',
  referenceNumber: 'div',
};

export const CUSTOM_COLOR_MAPPING: { [variant in CustomVariant]?: TypographyProps['color'] } = {
  h1: 'text.primary',
  h2: 'text.primary',
  h3: 'text.primary',
  h4: 'text.primary',
  h5: 'text.primary',

  h6: 'text.overline',

  body1: 'text.secondary', // P1
  body2: 'text.secondary', // P2
};

/**
 * The Typography
 *
 * @version [AIV]{version}[/AIV]
 * @author [SFP](https://gitlab.sfp.berlin/products/sparkasse.de/portals)
 * @see [https://styleguide.sparkasse.de/document/69#/ui-komponenten/typography]
 */
export const Typography: React.FC<TypographyProps> = ({
  children,
  component,
  variant,
  innerPadding = 'none',
  wordBreak = 'break-word',
  hyphens = 'auto',
  ...props
}) => {
  const shouldUseBlockDisplay = variant?.startsWith('c') || variant?.startsWith('h');

  const marginY = innerPadding === 'both' || innerPadding == 'vertical' ? DEFAULT_MARGIN_TEXT : undefined;
  const marginX = innerPadding === 'both' || innerPadding == 'horizontal' ? DEFAULT_MARGIN_TEXT : undefined;

  const isColoredVariant = variant && variant !== 'inherit' && variant !== 'paragraph';
  const isCustomVariant = variant && variant !== 'inherit' && variant !== 'paragraph';

  const sx = useMemo(() => {
    // TODO: TypeScript reports props?.sx['color'] as expression of type '"color"' can't be used to index type 'SystemCssProperties<Theme>
    // TODO: and Element implicitly has an 'any' type because expression of type 'OverridableStringUnion<"inherit" | Variant>
    // TODO: Figure out why this is happening and fix it
    const propsSx = props?.sx as unknown as { color?: string } | undefined;
    const variantColor: string | undefined = isColoredVariant ? (CUSTOM_COLOR_MAPPING[variant] as string) : undefined;
    const color: string | undefined = propsSx?.color || (props?.color as string) || variantColor;

    if (innerPadding === 'both' || innerPadding === 'vertical') {
      return {
        wordBreak,
        hyphens,
        'body.if6 &': {
          color,
          marginTop: props?.marginTop || props?.marginY || marginY,
          marginBottom: props?.marginBottom || props?.marginY || marginY,
          marginLeft: props?.marginLeft || props?.marginX || marginX,
          marginRight: props?.marginRight || props?.marginX || marginX,
        },
        [SSR_SAFE_FIRST_CHILD_SELECTOR]: {
          marginTop: 0,
        },
        [SSR_SAFE_LAST_CHILD_SELECTOR]: {
          marginBottom: 0,
        },
        ...props?.sx,
      };
    }
    return {
      wordBreak,
      hyphens,
      'body.if6 &': {
        color,
        marginTop: props?.marginTop || props?.marginY || marginY,
        marginBottom: props?.marginBottom || props?.marginY || marginY,
        marginLeft: props?.marginLeft || props?.marginX || marginX,
        marginRight: props?.marginRight || props?.marginX || marginX,
      },
      ...props?.sx,
    };
  }, [
    innerPadding,
    wordBreak,
    hyphens,
    props?.sx,
    props?.marginTop,
    props?.marginY,
    props?.marginBottom,
    props?.marginLeft,
    props?.marginX,
    props?.marginRight,
    props?.color,
    variant,
    marginY,
    marginX,
  ]);

  return (
    <MaterialTypography
      component={component || (isCustomVariant ? CUSTOM_VARIANT_MAPPING[variant] : 'p')}
      color={props?.color || (isColoredVariant ? CUSTOM_COLOR_MAPPING[variant] : undefined)}
      variant={variant}
      marginY={marginY}
      marginX={marginX}
      display={shouldUseBlockDisplay ? 'block' : undefined}
      {...props}
      sx={sx}
    >
      {children}
    </MaterialTypography>
  );
};
